import React, { useEffect, useState } from "react";

import {
  QUIZ_MIN_OPTIONS,
  QUIZ_OPTION_COUNT_CHOICES,
  QUIZ_OPTION_KEYS,
  QUIZ_OPTION_LABELS,
  QUIZ_OPTION_MAX,
  QUIZ_QUESTION_MAX,
  applyQuizOptionCount,
  clampQuizFields,
  emptyQuizQuestion,
  getQuizOptionValues,
  inferQuizOptionCount,
  validateQuizRow,
} from "./quizHelpers";

export default function QuizQuestionModal({
  open,
  mode,
  initial,
  onClose,
  onSubmit,
  isSaving,
  savingLabel,
}) {
  const [draft, setDraft] = useState(() => emptyQuizQuestion("temp"));
  const [optionCount, setOptionCount] = useState(QUIZ_MIN_OPTIONS);
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    if (!open) return;
    const base = initial
      ? { id: initial.id, ...clampQuizFields(initial) }
      : emptyQuizQuestion(`new-${Date.now()}`);
    const count = inferQuizOptionCount(base);
    setDraft(applyQuizOptionCount(base, count));
    setOptionCount(count);
    setLocalError("");
  }, [open, mode, initial]);

  if (!open) return null;

  const setField = (field, value, max = QUIZ_OPTION_MAX) => {
    setDraft((prev) => ({ ...prev, [field]: value.slice(0, max) }));
    setLocalError("");
  };

  const handleOptionCountChange = (nextCount) => {
    const n = Number(nextCount);
    setOptionCount(n);
    setDraft((prev) => applyQuizOptionCount(prev, n));
    setLocalError("");
  };

  const finalize = async () => {
    setLocalError("");

    const check = validateQuizRow(draft, "", { requireQuestion: true });
    if (!check.ok) {
      setLocalError(check.message);
      return;
    }

    try {
      await onSubmit({ id: draft.id, ...check.data });
    } catch {
      /* parent surfaces error */
    }
  };

  const isEdit = mode === "edit";
  const visibleOptionKeys = QUIZ_OPTION_KEYS.slice(0, optionCount);
  const filledOptions = getQuizOptionValues(draft);

  return (
    <div className="quiz-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="quiz-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="quiz-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="quiz-modal-header">
          <h2 id="quiz-modal-title">{isEdit ? "Edit Question" : "New Question"}</h2>
          <button type="button" className="quiz-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="quiz-modal-body">
          {localError ? <p className="cr-alert cr-alert-error quiz-modal-error">{localError}</p> : null}

          <div className="quiz-modal-field quiz-modal-field--full">
            <label htmlFor="quiz-modal-question">Question</label>
            <textarea
              id="quiz-modal-question"
              className="quiz-modal-textarea"
              rows={4}
              value={draft.question_name}
              maxLength={QUIZ_QUESTION_MAX}
              onChange={(e) => setField("question_name", e.target.value, QUIZ_QUESTION_MAX)}
              placeholder="Enter the question"
              disabled={isSaving}
            />
            <p className="quiz-modal-limit">Max length {QUIZ_QUESTION_MAX} characters</p>
          </div>

          <div className="quiz-modal-field quiz-modal-field--full">
            <label htmlFor="quiz-modal-option-count">Number of options</label>
            <select
              id="quiz-modal-option-count"
              className="quiz-modal-option-count"
              value={optionCount}
              onChange={(e) => handleOptionCountChange(e.target.value)}
              disabled={isSaving}
            >
              {QUIZ_OPTION_COUNT_CHOICES.map((n) => (
                <option key={n} value={n}>
                  {n} options
                </option>
              ))}
            </select>
            <p className="themeupdate-muted quiz-modal-option-count-hint">
              Choose 2, 3, or 4 answer choices. Extra options are cleared when you reduce the count.
            </p>
          </div>

          <div className={`quiz-modal-options-grid quiz-modal-options-grid--${optionCount}`}>
            {visibleOptionKeys.map((key, i) => (
              <div key={key} className="quiz-modal-field">
                <label htmlFor={`quiz-modal-${key}`}>{QUIZ_OPTION_LABELS[i]}</label>
                <input
                  id={`quiz-modal-${key}`}
                  type="text"
                  value={draft[key]}
                  maxLength={QUIZ_OPTION_MAX}
                  onChange={(e) => setField(key, e.target.value)}
                  placeholder={QUIZ_OPTION_LABELS[i]}
                  disabled={isSaving}
                />
                <p className="quiz-modal-limit">Max length {QUIZ_OPTION_MAX} characters</p>
              </div>
            ))}
          </div>

          <div className="quiz-modal-field quiz-modal-field--full">
            <span className="quiz-modal-correct-label">Correct answer</span>
            {filledOptions.length >= QUIZ_MIN_OPTIONS ? (
              <div className="quiz-modal-correct-options">
                {visibleOptionKeys.map((key) => {
                  const label = String(draft[key] ?? "").trim();
                  if (!label) return null;
                  return (
                    <label key={key} className="quiz-modal-correct-option">
                      <input
                        type="radio"
                        name="quiz-correct-answer"
                        value={label}
                        checked={draft.correct_answer === label}
                        onChange={() => {
                          setDraft((prev) => ({ ...prev, correct_answer: label }));
                          setLocalError("");
                        }}
                        disabled={isSaving}
                      />
                      <span>{label}</span>
                    </label>
                  );
                })}
              </div>
            ) : (
              <p className="themeupdate-muted">
                Fill in at least {QUIZ_MIN_OPTIONS} options to choose the correct answer.
              </p>
            )}
          </div>
        </div>

        <div className="quiz-modal-footer">
          <button
            type="button"
            className="quiz-modal-btn quiz-modal-btn-save"
            onClick={finalize}
            disabled={isSaving}
          >
            <i className="fa-solid fa-cloud-arrow-up" aria-hidden />
            {isSaving ? savingLabel || "Saving…" : isEdit ? "Update" : "Add New Question"}
          </button>
          <button type="button" className="quiz-modal-btn quiz-modal-btn-close" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
